import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { RotateCcw, Share2 } from "lucide-react";
import { toast } from "sonner";

const WORDS = [
  "ARCADE","PIXEL","LEVEL","SCORE","BRAIN","LASER","ROBOT","POWER","SPEED","BLAST",
  "QUEST","NINJA","TURBO","GHOST","FLASH","CRANE","SNAKE","MAGIC","TOKEN","COMBO",
  "BONUS","CRASH","DODGE","FLAME","GLORY","JOKER","KNACK","LUCKY","MEDAL","PRIZE",
  "RIVAL","SHARP","SONIC","TITAN","VAULT","WINGS","ZONES","CHASE","DREAM","FROST",
];
const ROWS = 6, COLS = 5;

function evaluate(guess, answer) {
  const res = Array(COLS).fill("absent");
  const a = answer.split("");
  const counts = {};
  a.forEach((c) => (counts[c] = (counts[c] || 0) + 1));
  guess.split("").forEach((c, i) => {
    if (a[i] === c) { res[i] = "correct"; counts[c]--; }
  });
  guess.split("").forEach((c, i) => {
    if (res[i] !== "correct" && counts[c] > 0) { res[i] = "present"; counts[c]--; }
  });
  return res;
}

const KEYS = ["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"];

export function WordGuess({ onScore }) {
  const [answer, setAnswer] = useState(() => WORDS[Math.floor(Math.random() * WORDS.length)]);
  const [guesses, setGuesses] = useState([]);
  const [evals, setEvals] = useState([]);
  const [current, setCurrent] = useState("");
  const [status, setStatus] = useState("playing"); // playing | won | lost
  const [letterState, setLetterState] = useState({});
  const reported = useRef(false);

  const reset = () => {
    setAnswer(WORDS[Math.floor(Math.random() * WORDS.length)]);
    setGuesses([]); setEvals([]); setCurrent(""); setStatus("playing");
    setLetterState({}); reported.current = false;
  };

  const submit = useCallback(() => {
    if (current.length !== COLS) { toast.error("Need 5 letters"); return; }
    const ev = evaluate(current, answer);
    const ng = [...guesses, current];
    const ne = [...evals, ev];
    setGuesses(ng); setEvals(ne);
    setLetterState((prev) => {
      const next = { ...prev };
      current.split("").forEach((c, i) => {
        const rank = { absent: 0, present: 1, correct: 2 };
        if (!next[c] || rank[ev[i]] > rank[next[c]]) next[c] = ev[i];
      });
      return next;
    });
    setCurrent("");
    if (current === answer) setStatus("won");
    else if (ng.length >= ROWS) setStatus("lost");
  }, [current, answer, guesses, evals]);

  const press = useCallback((key) => {
    if (status !== "playing") return;
    if (key === "ENTER") submit();
    else if (key === "DEL") setCurrent((c) => c.slice(0, -1));
    else if (/^[A-Z]$/.test(key)) setCurrent((c) => (c.length < COLS ? c + key : c));
  }, [status, submit]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Enter") press("ENTER");
      else if (e.key === "Backspace") press("DEL");
      else { const k = e.key.toUpperCase(); if (/^[A-Z]$/.test(k)) press(k); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [press]);

  useEffect(() => {
    if (status === "won" && !reported.current) {
      reported.current = true;
      const score = (ROWS - guesses.length + 1) * 100;
      onScore?.(score);
    }
    if (status === "lost") reported.current = true;
  }, [status, guesses.length, onScore]);

  const share = () => {
    const grid = evals.map((row) => row.map((s) => (s === "correct" ? "🟩" : s === "present" ? "🟨" : "⬛")).join("")).join("\n");
    const text = `Word Mania ${status === "won" ? guesses.length : "X"}/6\n${grid}\nPlay at Video Game Mania!`;
    if (navigator.share) navigator.share({ text }).catch(() => {});
    else { navigator.clipboard.writeText(text); toast.success("Result copied — share it!"); }
  };

  const colorFor = (s) => s === "correct" ? "#39FF14" : s === "present" ? "#EFFF04" : s === "absent" ? "#3f3f46" : "transparent";

  return (
    <div className="flex flex-col items-center" data-testid="word-game">
      <div className="grid gap-1.5 mb-6" style={{ gridTemplateRows: `repeat(${ROWS}, 1fr)` }}>
        {Array.from({ length: ROWS }).map((_, r) => (
          <div key={r} className="flex gap-1.5">
            {Array.from({ length: COLS }).map((_, c) => {
              const isCurrentRow = r === guesses.length && status === "playing";
              const ch = guesses[r]?.[c] || (isCurrentRow ? current[c] : "") || "";
              const ev = evals[r]?.[c];
              return (
                <div key={c} data-testid={`word-cell-${r}-${c}`}
                  className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center font-display font-black text-2xl uppercase border-2 transition-all"
                  style={{
                    background: ev ? colorFor(ev) : "transparent",
                    borderColor: ev ? colorFor(ev) : ch ? "#71717a" : "#27272a",
                    color: ev === "absent" ? "#a1a1aa" : ev ? "#000" : "#fff",
                  }}>
                  {ch}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {status !== "playing" && (
        <div className="text-center mb-5" data-testid="word-result">
          <p className={`font-display font-black text-2xl uppercase ${status === "won" ? "text-neon-lime" : "text-neon-pink"}`}>
            {status === "won" ? "You got it! 🎉" : `The word was ${answer}`}
          </p>
          <div className="flex gap-3 justify-center mt-3">
            <Button onClick={share} className="bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-10">
              <Share2 className="w-4 h-4 mr-1" /> Share
            </Button>
            <Button onClick={reset} className="bg-neon-cyan text-black rounded-none font-bold uppercase tracking-widest h-10">
              <RotateCcw className="w-4 h-4 mr-1" /> New Word
            </Button>
          </div>
        </div>
      )}

      {/* On-screen keyboard */}
      <div className="space-y-1.5 w-full max-w-md">
        {KEYS.map((row, ri) => (
          <div key={ri} className="flex gap-1.5 justify-center">
            {ri === 2 && <KeyBtn label="ENTER" onClick={() => press("ENTER")} wide />}
            {row.split("").map((k) => (
              <KeyBtn key={k} label={k} state={letterState[k]} onClick={() => press(k)} />
            ))}
            {ri === 2 && <KeyBtn label="DEL" onClick={() => press("DEL")} wide />}
          </div>
        ))}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-4">Type a guess, hit Enter. Green = right spot, Yellow = wrong spot.</p>
    </div>
  );
}

function KeyBtn({ label, state, onClick, wide }) {
  const bg = state === "correct" ? "#39FF14" : state === "present" ? "#EFFF04" : state === "absent" ? "#27272a" : "#3f3f46";
  const color = state === "correct" || state === "present" ? "#000" : "#fff";
  return (
    <button onClick={onClick} data-testid={`word-key-${label}`}
      className={`h-11 rounded font-bold text-sm uppercase transition-transform active:scale-95 ${wide ? "px-3" : "flex-1 min-w-[28px]"}`}
      style={{ background: bg, color }}>
      {label === "DEL" ? "⌫" : label}
    </button>
  );
}
